import { useEffect } from "react";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import {
  useFTSSearchWithFullRows,
  type UseFTSSearchWithFullRowsOptions,
} from "@/lib/hooks/api/search/use-fts-search-with-full-rows";

export type FTSEntityType = UseFTSSearchWithFullRowsOptions["entityType"];

export interface UseFTSWithDebouncedSearchOptions {
  /** FTS entity endpoint key (e.g. "contacts", "tickets"). */
  entityType: FTSEntityType;
  /** Result limit per FTS query. Default 50. */
  ftsLimit?: number;
  /** Debounce for the FTS hook. Default 300. */
  ftsDebounceMs?: number;
  /** Debounce for the search input. Default 300. */
  searchDelayMs?: number;
  /** Minimum trimmed search length to be considered "active". Default 1 (matches `> 1` i.e. 2+ chars). */
  minSearchLen?: number;
}

export interface UseFTSWithDebouncedSearchReturn<TRow> {
  searchValue: string;
  debouncedSearchValue: string;
  setSearchValue: (value: string) => void;
  hasSearchTerm: boolean;
  ftsResults: TRow[];
  ftsLoading: boolean;
}

/**
 * Combines `useDebouncedSearch` and `useFTSSearchWithFullRows`: maintains
 * debounced search input state and triggers FTS whenever the trimmed
 * debounced value exceeds `minSearchLen`. Used by select components that
 * branch between FTS-search mode and paginated-browse mode.
 */
export function useFTSWithDebouncedSearch<TRow = unknown>(
  options: UseFTSWithDebouncedSearchOptions
): UseFTSWithDebouncedSearchReturn<TRow> {
  const {
    entityType,
    ftsLimit = 50,
    ftsDebounceMs = 300,
    searchDelayMs = 300,
    minSearchLen = 1,
  } = options;

  const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
    initialValue: "",
    delay: searchDelayMs,
  });

  const hasSearchTerm = (debouncedSearchValue?.trim() ?? "").length > minSearchLen;

  const { search, results, isLoading } = useFTSSearchWithFullRows({
    entityType,
    limit: ftsLimit,
    debounceMs: ftsDebounceMs,
  });

  useEffect(() => {
    if (hasSearchTerm) search(debouncedSearchValue);
  }, [hasSearchTerm, debouncedSearchValue, search]);

  return {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
    hasSearchTerm,
    ftsResults: results as TRow[],
    ftsLoading: isLoading,
  };
}
